/**
 * Missions API - Driver Mono View + Mission Management
 * @module api/missions
 */

import { apiRequest, getAuthHeaders } from './core.js';

// =============================================================================
// DRIVER MONO VIEW API (self-service for drivers)
// =============================================================================

export const driverMonoAPI = {
  getProfile: () => apiRequest('/drivers/me'),
  getVehicle: () => apiRequest('/drivers/me/vehicle'),
  getMissions: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/drivers/me/missions${qs ? `?${qs}` : ''}`);
  },
  getMissionDetail: (id) => apiRequest(`/drivers/me/missions/${id}`),
  acceptMission: (id) => apiRequest(`/drivers/me/missions/${id}/accept`, { method: 'PUT' }),
  startMission: (id) => apiRequest(`/drivers/me/missions/${id}/start`, { method: 'PUT' }),
  completeMission: (id) => apiRequest(`/drivers/me/missions/${id}/complete`, { method: 'PUT' }),
  getCosts: () => apiRequest('/drivers/me/costs'),
  getDocuments: () => apiRequest('/drivers/me/documents'),
  getStats: () => apiRequest('/drivers/me/stats'),
  getFuelLogs: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/drivers/me/fuel${qs ? `?${qs}` : ''}`);
  },
  getFuelPrefill: () => apiRequest('/drivers/me/fuel/prefill'),
  submitFuelFill: (formData) => {
    const base = process.env.NODE_ENV === 'production'
      ? (process.env.REACT_APP_API_URL || '/api/v1')
      : '/api/v1';
    return fetch(`${base}/drivers/me/fuel`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    }).then(r => r.json());
  },
  getFines: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/drivers/me/fines${qs ? `?${qs}` : ''}`);
  },
  declareClaim: (data) => apiRequest('/drivers/me/claims', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateAvailability: (data) => apiRequest('/drivers/me/availability', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  requestMaintenance: (data) => apiRequest('/drivers/me/maintenance', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// =============================================================================
// MISSION MANAGEMENT API (for managers)
// =============================================================================

export const missionAPI = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/missions${qs ? `?${qs}` : ''}`);
  },
  getById: (id) => apiRequest(`/missions/${id}`),
  create: (data) => apiRequest('/missions', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/missions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  assign: (id, data) => apiRequest(`/missions/${id}/assign`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/missions/${id}`, {
    method: 'DELETE',
  }),

  // Trajet management
  addTrajet: (missionId, data) => apiRequest(`/missions/${missionId}/trajets`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateTrajet: (missionId, trajetId, data) => apiRequest(`/missions/${missionId}/trajets/${trajetId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteTrajet: (missionId, trajetId) => apiRequest(`/missions/${missionId}/trajets/${trajetId}`, {
    method: 'DELETE',
  }),
  reorderTrajets: (missionId, trajetIds) => apiRequest(`/missions/${missionId}/trajets/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ trajet_ids: trajetIds }),
  }),

  // PDF
  getFichePdf: (id) => `${process.env.REACT_APP_API_URL || '/api/v1'}/missions/${id}/fiche-pdf`,
};
